import { createDomain, sample } from 'effector'
import { interval } from 'patronum'
import { haveSameFiles } from '~/features/DocumentBasedRegistration/helpers'
import {
  AxiosErrorType,
  IdentityRecognitionRequest,
  IdentityRecognitionRequestAttributes,
  RecognitionStatusEnum,
  RecognitionErrorsResponse,
} from '~/shared/api'
import { IdentityRecognitionDocumentEnum } from '~/shared/config/enums'

type RecognizedDocuments = {
  recognitionId: number
  status: RecognitionStatusEnum
  documentType: IdentityRecognitionDocumentEnum
  filesIds: string[]
  response: RecognitionErrorsResponse | null
  secretCode: string
}

type RecordRecognizedDocuments = Partial<
  Record<IdentityRecognitionDocumentEnum, RecognizedDocuments>
> | null

const domain = createDomain('features.recognized')

export const sendPassportForRecognition =
  domain.createEvent<IdentityRecognitionRequestAttributes>()

export const sendDocumentForRecognitionFx = domain.createEffect<
  IdentityRecognitionRequestAttributes,
  {
    documentType: IdentityRecognitionDocumentEnum
    recognitionId: number
    filesIds: string[]
    secretCode: string
  },
  AxiosErrorType
>({
  handler: async (values) => {
    const res = await IdentityRecognitionRequest.sendDocumentForRecognition(
      values,
    )
    return {
      documentType: values.documentType,
      recognitionId: res.id,
      filesIds: values.files.map(({ uuid }) => uuid),
      secretCode: res.secretCode,
    }
  },
})

export const checkDocumentsRecognitionStatusFx = domain.createEffect<
  RecordRecognizedDocuments,
  RecordRecognizedDocuments,
  AxiosErrorType
>({
  async handler(documents) {
    const promises = Object.values(documents || {})
      .filter(({ status }) => status === RecognitionStatusEnum.pending)
      .map(async (document) => {
        try {
          const res =
            await IdentityRecognitionRequest.resultDocumentForRecognition(
              document.recognitionId,
              document.secretCode,
            )
          return {
            ...document,
            status: res.status as RecognitionStatusEnum,
            response: res.recognitionResult,
          }
        } catch (e) {
          return {
            ...document,
            status: 'error',
          }
        }
      })

    const results = await Promise.all(promises)

    return results.reduce<RecordRecognizedDocuments>((acc, documents) => {
      return {
        ...(acc || {}),
        [documents.documentType]: documents,
      }
    }, null)
  },
})

export const $recognizedDocuments = domain
  .createStore<RecordRecognizedDocuments>(null)
  .on(sendDocumentForRecognitionFx.doneData, (store, document) => ({
    ...store,
    [document.documentType]: {
      ...document,
      status: RecognitionStatusEnum.pending,
    },
  }))
  .on(checkDocumentsRecognitionStatusFx.doneData, (store, documents) => ({
    ...(store || {}),
    ...(documents || {}),
  }))

sample({
  clock: sendPassportForRecognition,
  source: $recognizedDocuments,
  filter: (recognizedDocuments, values) => {
    if (!recognizedDocuments) return true

    const prev = recognizedDocuments[values.documentType]
    if (prev?.status === RecognitionStatusEnum.error) return true

    return !haveSameFiles(
      prev?.filesIds || [],
      values.files.map(({ uuid }) => uuid),
    )
  },
  fn: (_, values) => values,
  target: sendDocumentForRecognitionFx,
})

export const $recognitionErrors = $recognizedDocuments.map((docs) => {
  if (!docs) return null

  return Object.values(docs).reduce<RecognitionErrorsResponse | null>(
    (acc, doc) => ({
      ...acc,
      ...doc.response,
    }),
    null,
  )
})

export const $recognizedEntities = $recognizedDocuments.map((docs) => {
  if (!docs) return {}

  return Object.values(docs).reduce<Record<string, string>>((acc, doc) => {
    if (doc.response) {
      Object.values(doc.response).forEach((result) => {
        if (result?.entities) {
          Object.assign(acc, result.entities)
        }
      })
    }
    return acc
  }, {})
})

const startPollingRecognition = domain.createEvent()
const stopPollingRecognition = domain.createEvent()

const recognitionPollingTimer = interval({
  timeout: 5000,
  start: startPollingRecognition,
  stop: stopPollingRecognition,
  leading: false,
  trailing: false,
})

sample({
  clock: $recognizedDocuments,
  filter: (documents) =>
    !!documents &&
    Object.values(documents).some(
      (document) => document.status === RecognitionStatusEnum.pending,
    ),
  target: startPollingRecognition,
})

sample({
  clock: $recognizedDocuments,
  filter: (documents) =>
    !documents ||
    Object.values(documents).every(
      (document) => document.status === RecognitionStatusEnum.completed,
    ),
  target: stopPollingRecognition,
})

sample({
  clock: recognitionPollingTimer.tick,
  source: $recognizedDocuments,
  target: checkDocumentsRecognitionStatusFx,
})
